import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { ArrowLeft, CheckCircle, Info, FileText, Loader2, AlertCircle, Clock, User, Hash, FileCode, Package, Wrench } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import auditService from '../auditService';

// Utility function for class names
function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}


// Form Details Modal Component (reuse existing)
const FormDetailsModal = ({ selectedForm, auditFormData, isLoadingFormData, formDataError }) => {
  const { t } = useTranslation();
  if (isLoadingFormData) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        <span>{t('summary_item.loading_form_details')}</span>
      </div>
    );
  }

  if (formDataError) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 mb-2">{t('summary_item.error_loading_details')}</div>
        <div className="text-sm text-gray-500">{formDataError}</div>
      </div>
    );
  }

  if (!auditFormData?.data) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-500">{t('summary_item.no_detailed_data')}</div>
      </div>
    );
  }

  const formDetails = auditFormData.data;

  return (
    <ScrollArea className="max-h-[60vh] pr-4">
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-500">{t('summary_item.form_name')}</label>
            <p className="text-lg font-semibold">{formDetails.form_name || 'N/A'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">{t('summary_item.form_code')}</label>
            <p className="text-lg font-semibold">{formDetails.form_code || 'N/A'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">{t('summary_item.italian_name')}</label>
            <p className="text-lg font-semibold">{formDetails.form_name_italian || 'N/A'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">{t('summary_item.total_fields')}</label>
            <p className="text-lg font-semibold">{formDetails.total_fields_count || 0}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">{t('summary_item.total_points')}</label>
            <p className="text-lg font-semibold">{formDetails.total_possible_points || 0}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">{t('summary_item.passing_threshold')}</label>
            <p className="text-lg font-semibold">{formDetails.passing_threshold || 0}%</p>
          </div>
        </div>
      </div>
    </ScrollArea>
  );
};

// Loading Screen Component
const LoadingScreen = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
        <p className="text-lg text-muted-foreground">{t('summary_item.loading_results')}</p>
      </div>
    </div>
  );
};

// Error Screen Component
const ErrorScreen = ({ error, onRetry, onGoBack }) => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
        <h2 className="text-2xl font-bold mb-2">{t('summary_item.error_loading_results')}</h2>
        <p className="text-muted-foreground mb-6">{error}</p>
        <div className="space-y-3">
          {onRetry && (
            <Button onClick={onRetry} variant="outline" className="w-full">
              {t('summary_item.try_again')}
            </Button>
          )}
          {onGoBack && (
            <Button onClick={onGoBack} variant="default" className="w-full">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('summary_item.go_back_dashboard')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Component
export default function SummaryItemPage({ 
  formData, 
  onGoBack, 
  onComplete, 
  selectedForm, 
  auditFormData,
  auditResponseId,
  currentAuditResponse,
  onViewCompletedAudit,
  isReadOnly,
  ...otherProps
}) {
  const { t } = useTranslation();
  // IMMEDIATE debug logging at component entry
  console.log('🚨 SUMMARY PAGE ENTRY DEBUG:');
  console.log('auditResponseId:', auditResponseId);
  console.log('currentAuditResponse:', currentAuditResponse);
  console.log('formData:', formData);
  console.log('otherProps keys:', Object.keys(otherProps));
  console.log('otherProps values:', otherProps);
  
  // Check if we're in a problematic state immediately
  if (!auditResponseId && !currentAuditResponse?.audit_form_response_id) {
    console.error('🚨 CRITICAL: No audit response ID available at component entry!');
  }
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [auditResponse, setAuditResponse] = useState(currentAuditResponse || null);

  // Extract response ID from various sources with debugging
  const responseId = auditResponseId || 
                    formData?.auditResponseId || 
                    formData?.response_id ||
                    formData?.audit_form_response_id ||
                    currentAuditResponse?.audit_form_response_id ||
                    currentAuditResponse?.id ||
                    currentAuditResponse?.response_id ||
                    otherProps.responseId ||
                    otherProps.auditResponseId;

  // Enhanced debug logging
  console.log('🔍 SummaryItemPage props debug:', {
    auditResponseId,
    'formData.auditResponseId': formData?.auditResponseId,
    'formData.response_id': formData?.response_id,
    currentAuditResponse: currentAuditResponse ? {
      id: currentAuditResponse.audit_form_response_id,
      status: currentAuditResponse.status,
      fullObject: currentAuditResponse
    } : null,
    'otherProps.responseId': otherProps.responseId,
    extractedResponseId: responseId,
    allOtherProps: Object.keys(otherProps)
  });
  
  // Log all props passed to this component
  console.log('🔍 All SummaryItemPage props:', {
    formData, 
    auditResponseId,
    currentAuditResponse,
    selectedForm,
    auditFormData,
    ...otherProps
  });

  useEffect(() => {
    if (responseId) {
      // Always fetch fresh data when component mounts or responseId changes
      console.log('Fetching audit response for ID:', responseId);
      fetchAuditResponse();
    } else if (!auditResponse && !currentAuditResponse) {
      console.error('❌ No audit response ID found. Available data:', {
        auditResponseId,
        formData,
        currentAuditResponse,
        otherProps
      });
      
      // Try to fetch the latest audit response using formData info as fallback
      const tryFallbackFetch = async () => {
        console.log('🔄 Attempting fallback fetch using formData...');
        
        // Try to get the latest audit response based on unit details
        if (formData?.serialNumber || formData?.workOrder) {
          try {
            console.log('🔍 Searching for audit response by unit details:', {
              serialNumber: formData.serialNumber,
              workOrder: formData.workOrder,
              unitModelId: formData.unitModelId,
              productionLineId: formData.productionLineId
            });
            
            // Call auditService to get recent audit responses
            const recentAudits = await auditService.fetchAllDraftAudits();
            
            if (recentAudits.success && recentAudits.audits?.length > 0) {
              // Find the most recent audit that matches our criteria
              const matchingAudit = recentAudits.audits.find(audit => 
                audit.serial_number === formData.serialNumber ||
                audit.work_order === formData.workOrder
              );
              
              if (matchingAudit) {
                console.log('✅ Found matching audit response:', matchingAudit);
                setAuditResponse(matchingAudit);
                setIsLoading(false);
                return;
              }
            }
            
            console.log('⚠️ No matching audit found in recent audits');
          } catch (fallbackError) {
            console.error('❌ Fallback fetch failed:', fallbackError);
          }
        }
        
        // If fallback fails, wait a bit more for currentAuditResponse to be populated
        setTimeout(() => {
          if (!currentAuditResponse && !responseId) {
            console.error('❌ All fallback mechanisms failed. No valid audit response found.');
            console.error('🚨 TIMEOUT ERROR - Setting fallback error message');
            setError('Unable to load audit summary. The audit response data is not available.');
            setIsLoading(false);
          }
        }, 3000); // Wait 3 seconds total
      };
      
      // Execute fallback after a short delay
      setTimeout(tryFallbackFetch, 1000);
    } else if (currentAuditResponse && !auditResponse) {
      // Use currentAuditResponse as initial data if available
      console.log('Using currentAuditResponse as initial data:', currentAuditResponse);
      setAuditResponse(currentAuditResponse);
      setIsLoading(false);
    }
  }, [responseId]);

  // Additional effect to monitor currentAuditResponse changes when responseId is initially missing
  useEffect(() => {
    if (!responseId && currentAuditResponse?.audit_form_response_id) {
      console.log('Current audit response became available, refetching...');
      // Trigger a re-evaluation by updating a dependency
      const newResponseId = currentAuditResponse.audit_form_response_id;
      if (newResponseId) {
        fetchAuditResponse();
      }
    }
  }, [currentAuditResponse?.audit_form_response_id]);

  // Force refresh when currentAuditResponse changes and we have a responseId
  useEffect(() => {
    if (currentAuditResponse && responseId && currentAuditResponse.audit_form_response_id === responseId) {
      console.log('Current audit response updated, fetching fresh data...');
      fetchAuditResponse();
    }
  }, [currentAuditResponse?.status, currentAuditResponse?.updated_at]);

  const fetchAuditResponse = async (retryCount = 0) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Guard against undefined responseId
      if (!responseId) {
        throw new Error('Cannot fetch audit response: responseId is undefined or null');
      }
      
      console.log(`Fetching audit response for ID: ${responseId} (attempt ${retryCount + 1})`);
      
      // Use auditService which handles the correct API endpoints and authentication
      const result = await auditService.getAuditResponse(responseId, { useCache: false });
      
      if (result.success && result.auditResponse) {
        setAuditResponse(result.auditResponse);
        console.log('Audit response loaded:', result.auditResponse);
        
        // If this looks like stale data (e.g., still in DRAFT status but should be completed)
        // and we have fewer than 3 retries, try again after a short delay
        if (retryCount < 2 && currentAuditResponse?.status && 
            result.auditResponse.status === 'DRAFT' && 
            currentAuditResponse.status !== 'DRAFT') {
          
          console.log('Data appears stale, retrying in 1 second...');
          setTimeout(() => {
            fetchAuditResponse(retryCount + 1);
          }, 1000);
          return;
        }
        
      } else {
        throw new Error(result.error || 'Invalid response format');
      }
    } catch (err) {
      console.error('Failed to fetch audit response:', err);
      
      // If we have retries left and this might be a timing issue, try again
      if (retryCount < 2) {
        console.log('Retrying fetch in 2 seconds...');
        setTimeout(() => {
          fetchAuditResponse(retryCount + 1);
        }, 2000);
        return;
      }
      
      const errorMessage = err.message || 'Failed to load audit results';
      console.error('🚨 SETTING ERROR:', errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading screen
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Show error screen
  if (error) {
    return <ErrorScreen error={error} onRetry={responseId ? fetchAuditResponse : null} onGoBack={onGoBack} />;
  }

  // Extract data from the audit response
  const finalScore = auditResponse?.total_points_achieved || 0;
  const maxScore = auditResponse?.total_points_possible || 100;
  const scorePercentage = auditResponse?.score_percentage || 0;
  const passThreshold = auditResponse?.pass_threshold || 100;
  const overallResult = auditResponse?.overall_result || 'PENDING';
  
  // Get issues count from field_responses or summary_stats
  const issuesDetected = auditResponse?.field_responses?.length || 
                        auditResponse?.summary_stats?.failed_checks || 
                        0;
  
  const totalChecks = auditResponse?.summary_stats?.total_checks || 0;
  const passedChecks = auditResponse?.summary_stats?.passed_checks || 0;

  // Format data for display
  const form = selectedForm || auditResponse?.audit_form;
  const submittedDate = formatDateTime(auditResponse?.submitted_at || auditResponse?.created_at);

  return (
    <main className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <header className="text-center">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{t('summary_item.audit_summary')}</h1>
          <p className="text-gray-600">{t('summary_item.control_items_summary')}</p>
        </header>

        {/* Audit Status Bar */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-6">
                <div>
                  <span className="text-sm text-blue-700">{t('summary_item.status')}:</span>
                  <Badge variant={auditResponse?.status === 'SUBMITTED' ? 'default' : 'secondary'} className="ml-2">
                    {auditResponse?.status || 'UNKNOWN'}
                  </Badge>
                </div>
                <div>
                  <span className="text-sm text-blue-700">Result:</span>
                  <Badge variant={overallResult === 'PASS' ? 'default' : 'destructive'} className="ml-2">
                    {overallResult}
                  </Badge>
                </div>
                <div className="text-sm">
                  <span className="text-blue-700">Submitted:</span>
                  <span className="text-blue-900 font-medium ml-2">{submittedDate}</span>
                </div>
              </div>
              {form && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setIsFormModalOpen(true)}
                  className="bg-white"
                >
                  <Info className="h-4 w-4 mr-2" />
                  {t('summary_item.form_details')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Audit Details (2 columns wide) */}
          <div className="lg:col-span-2 space-y-4">
            {/* Unit Information */}
            <Card>
              <CardHeader className="pb-3">
                <h3 className="font-semibold text-gray-900">Unit Information</h3>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Serial Number</p>
                    <p className="font-medium">{formData?.serialNumber || auditResponse?.serial_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Unit Model</p>
                    <p className="font-medium">{formData?.unitModel || auditResponse?.unit_model || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Date and Time</p>
                    <p className="font-medium">{submittedDate}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Work Order (OV)</p>
                    <p className="font-medium">{formData?.workOrder || auditResponse?.work_order || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Production Line</p>
                    <p className="font-medium">{formData?.productionLine || auditResponse?.production_line || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Unit Lock</p>
                    <p className="font-medium">{formData?.unitLock || auditResponse?.unit_lock || 'N/A'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Audit Details */}
            <Card>
              <CardHeader className="pb-3">
                <h3 className="font-semibold text-gray-900">{t('summary_item.audit_details')}</h3>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Operator</p>
                    <p className="font-medium">{getOperatorName(auditResponse)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">{t('summary_item.audit_form')}</p>
                    <p className="font-medium">{form?.form_name || form?.title || 'N/A'}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">{t('summary_item.audit_session_id')}</p>
                    <p className="font-medium text-sm">{auditResponse?.audit_form_response_id || auditResponse?.submission_id || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Form ID</p>
                    <p className="font-medium">{form?.id || form?.form_id || auditResponse?.audit_form_id || 'N/A'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Score Cards */}
          <div className="space-y-4">
            {/* Final Score Card */}
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardHeader className="text-center pb-2">
                <h3 className="text-lg font-semibold text-gray-900">Final Score</h3>
              </CardHeader>
              <CardContent className="text-center">
                <div className="text-5xl font-bold text-blue-600 mb-2">
                  {finalScore}
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Max points - {maxScore}
                </p>
                <Badge 
                  variant={overallResult === 'PASS' ? 'default' : 'destructive'}
                  className="text-base px-3 py-1"
                >
                  {overallResult === 'PASS' ? '✓ PASS' : '✗ FAIL'}
                </Badge>
                <p className="text-xs text-gray-500 mt-2">
                  Passing Threshold: {passThreshold}%
                </p>
              </CardContent>
            </Card>

            {/* Issues Summary Card */}
            <Card className={cn(
              "border-2",
              issuesDetected === 0 ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"
            )}>
              <CardHeader className="pb-3">
                <h3 className="font-semibold text-gray-900">
                  {t('summary_item.failed_control_items')}: 
                  <span className={cn(
                    "ml-2 text-2xl",
                    issuesDetected === 0 ? "text-green-600" : "text-red-600"
                  )}>
                    {totalChecks-passedChecks}
                  </span>
                </h3>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Control Items:</span>
                  <span className="font-medium">{totalChecks}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Passed Control Items:</span>
                  <span className="font-medium text-green-600">{passedChecks}</span>
                </div>
                {auditResponse?.summary_stats?.critical_issues > 0 && (
                  <div className="flex justify-between text-sm pt-2 border-t">
                    <span className="text-gray-600">Critical Issues:</span>
                    <span className="font-medium text-red-600">
                      {auditResponse.summary_stats.critical_issues}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <Button
            variant="outline"
            size="lg"
            onClick={onGoBack}
            className="flex-1 h-12"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {isReadOnly ? t('summary_item.go_back_dashboard') : t('summary_item.go_back_audit')}
          </Button>

          {/* Status indicator - not a button */}
          <div className="flex-1 h-12 flex items-center justify-center bg-green-50 border-2 border-green-500 rounded-lg px-4">
            <CheckCircle className="mr-2 h-5 w-5 text-green-600" />
            <span className="text-lg font-semibold text-green-700">
              {t('summary_item.audit_completed')}
            </span>
          </div>
        </div>
      </div>

      {/* Form Details Modal */}
      <Dialog open={isFormModalOpen} onOpenChange={setIsFormModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {form?.form_name || form?.title} - {t('summary_item.details')}
            </DialogTitle>
          </DialogHeader>
          <FormDetailsModal 
            selectedForm={form}
            auditFormData={auditFormData || { data: auditResponse?.audit_form }}
            isLoadingFormData={false}
            formDataError={null}
          />
        </DialogContent>
      </Dialog>
    </main>
  );
}

// Helper functions
function formatDateTime(dateString) {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return dateString;
  }
}

function getOperatorName(auditResponse) {
  if (!auditResponse?.operator) return 'N/A';
  
  const operator = auditResponse.operator;
  const firstName = operator.first_name || operator.user?.first_name || '';
  const lastName = operator.last_name || operator.user?.last_name || '';
  const username = operator.username || operator.user?.username || '';
  
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || username || 'Unknown Operator';
}